import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface AnalyzingScreenProps {
  onComplete: () => void;
}
const STEPS = [{
  label: 'Procesando tus respuestas',
  delay: 0
}, {
  label: 'Detectando tu perfil emprendedor',
  delay: 900
}, {
  label: 'Analizando tu nivel de tiempo y experiencia',
  delay: 1900
}, {
  label: 'Calculando tu potencial de ventas',
  delay: 2900
}, {
  label: 'Preparando tu sistema personalizado',
  delay: 3900
}];
const TOTAL_DURATION = 5000;
export function AnalyzingScreen({
  onComplete
}: AnalyzingScreenProps) {
  const [progress, setProgress] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  // Progress bar smooth fill
  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, elapsed / TOTAL_DURATION * 100);
      setProgress(pct);
      if (pct >= 100) clearInterval(interval);
    }, 40);
    return () => clearInterval(interval);
  }, []);
  // Step activation + completion timeline
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    STEPS.forEach((step, idx) => {
      // activate this step
      timers.push(setTimeout(() => {
        setActiveStep(idx);
      }, step.delay));
      // mark this step complete ~800ms after it activates
      timers.push(setTimeout(() => {
        setCompletedSteps(prev => [...prev, idx]);
      }, step.delay + 800));
    });
    // finish & advance to results
    timers.push(setTimeout(() => {
      onComplete();
    }, TOTAL_DURATION + 400));
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);
  return <div className="flex flex-col min-h-[calc(100vh-50px)] bg-gradient-to-b from-[#f4ebd0] to-[#e8dcb8] px-6 py-10 max-w-md mx-auto w-full relative overflow-hidden" data-id="element-10">
      {/* Subtle wood texture */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: 'url("https://www.transparenttextures.com/patterns/wood-pattern.png")'
    }} data-id="element-11" />

      {/* Ambient glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-72 h-72 bg-amber-400/20 blur-3xl rounded-full pointer-events-none" data-id="element-12" />

      <div className="relative z-10 flex flex-col items-center justify-center flex-grow text-center" data-id="element-13">
        {/* Animated logo / icon */}
        <motion.div initial={{
        scale: 0.5,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="relative mb-8" data-id="element-14">
          {/* Outer rotating ring */}
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear'
        }} className="w-28 h-28 rounded-full border-[3px] border-amber-300/40 border-t-amber-700 border-r-amber-700" data-id="element-15" />
          {/* Inner counter-rotating ring */}
          <motion.div animate={{
          rotate: -360
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute inset-3 rounded-full border-[2px] border-amber-200/50 border-b-amber-600 border-l-amber-600" data-id="element-16" />
          {/* Center emoji */}
          <motion.div animate={{
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 1.6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 flex items-center justify-center text-4xl" data-id="element-17">
            🔮
          </motion.div>
        </motion.div>

        {/* Heading */}
        <motion.h2 initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.2
      }} className="text-2xl font-extrabold text-amber-950 leading-tight mb-2" data-id="element-18">
          Analizando tus respuestas...
        </motion.h2>
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.4,
        delay: 0.35
      }} className="text-sm text-amber-800/80 font-medium mb-8 max-w-xs" data-id="element-19">
          Estamos preparando tu plan personalizado para que empieces esta misma
          semana.
        </motion.p>

        {/* Progress bar */}
        <div className="w-full max-w-xs mb-8" data-id="element-20">
          <div className="h-2.5 w-full bg-amber-200/50 rounded-full overflow-hidden shadow-inner" data-id="element-21">
            <motion.div className="h-full bg-gradient-to-r from-amber-600 to-amber-800 rounded-full relative" style={{
            width: `${progress}%`
          }} data-id="element-22">
              <div className="absolute inset-0 bg-white/20 animate-pulse" data-id="element-23" />
            </motion.div>
          </div>
          <p className="text-[11px] font-bold text-amber-900 mt-2 tabular-nums" data-id="element-24">
            {Math.round(progress)}%
          </p>
        </div>

        {/* Checklist */}
        <div className="w-full max-w-xs flex flex-col gap-2.5 text-left" data-id="element-25">
          {STEPS.map((step, idx) => {
          const isCompleted = completedSteps.includes(idx);
          const isActive = activeStep === idx && !isCompleted;
          const isPending = !isCompleted && !isActive;
          return <AnimatePresence key={idx} data-id="element-26">
                {(isActive || isCompleted) && <motion.div initial={{
              opacity: 0,
              x: -15
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.35
            }} className="flex items-center gap-3" data-id="element-27">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-300 ${isCompleted ? 'bg-emerald-600 text-white' : 'bg-amber-100 border-2 border-amber-700'}`} data-id="element-28">
                      {isCompleted ? <motion.svg initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 15
                }} className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-29">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-30" />
                        </motion.svg> : <motion.div animate={{
                  rotate: 360
                }} transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  ease: 'linear'
                }} className="w-3 h-3 rounded-full border-2 border-amber-700 border-t-transparent" data-id="element-31" />}
                    </div>
                    <span className={`text-sm font-semibold transition-colors duration-300 ${isCompleted ? 'text-stone-600' : 'text-amber-950'}`} data-id="element-32">
                      {step.label}
                    </span>
                  </motion.div>}
              </AnimatePresence>;
        })}
        </div>
      </div>
    </div>;
}